import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { cloneDeep } from 'lodash';
import * as moment from 'moment';
import { NgForage } from 'ngforage';
import AssetHelper from 'src/app/helpers/asset-helper';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import { KeysOfType } from 'src/app/helpers/utility-functions';
import Asset from 'src/app/model/asset';
import Employee from 'src/app/model/product/employee';
import { User } from 'src/app/model/user';
import { AllEmployeesControllerRequestService } from 'src/app/services/api/employee/all-employees-controller-request.service';
import { AuthService } from 'src/app/services/auth.service';
import { ModalService } from 'src/app/services/modal.service';
import { SnackService } from 'src/app/services/snack.service';
import { ImmoTaskOverviewControllerRequestService } from '../../overview/immo-task/service/immo-task-overview-controller-request.service';
import { IPInstallerListControllerRequestService } from '../../tour/tour-page/service/ip-installer-list-controller-request.service';
import { CreateEditImmoTaskControllerRequestService } from './service/create-edit-immo-task/create-edit-immo-task-controller-request.service';
import { GetImmoTaskForEditControllerRequestService } from './service/get-immo-task-for-edit/get-immo-task-for-edit-controller-request.service';
import {
  getImmoTaskEditableFromForEdit,
  getNewEditable,
  ImmoTaskEditable,
  ImmoTaskForEdit,
} from './service/get-immo-task-for-edit/immo-task-for-edit';
import { ImmoTaskType } from '../../overview/immo-task/model/immo-task-type';

@Component({
  selector: 'create-edit-task-modal',
  templateUrl: './create-edit-task-modal.component.html',
  styleUrls: ['./create-edit-task-modal.component.scss'],
})
export class CreateEditTaskModalComponent implements OnInit, OnDestroy {
  @Input()
  taskId: number;

  @Input()
  placerInput: Employee;

  @Input()
  tourDate: moment.Moment;

  @Input()
  focusSubjectField: boolean;

  @Output()
  result = new EventEmitter<'taskCreatedOrEdited' | 'taskArchived' | null>();

  task: ImmoTaskEditable;

  loading: boolean = true;

  employeeChoices: Employee[];
  placerChoices: Employee[];

  submitting: boolean = false;
  archiving: boolean = false;
  placer: Employee;

  today = new Date();

  DEFAULT_EXECUTION_TIME = '00:00';
  executionTime = this.DEFAULT_EXECUTION_TIME;
  availableOptions: string[];
  selected: ImmoTaskType;

  constructor(
    private matDialogRef: MatDialogRef<CreateEditTaskModalComponent>,
    private getImmoTaskForEditControllerRequestService: GetImmoTaskForEditControllerRequestService,
    private createEditImmoTaskControllerRequestService: CreateEditImmoTaskControllerRequestService,
    private immoTaskOverviewControllerRequestService: ImmoTaskOverviewControllerRequestService,
    private allEmployeesControllerRequestService: AllEmployeesControllerRequestService,
    private ipInstallerListControllerRequestService: IPInstallerListControllerRequestService,
    private ngForage: NgForage,
    private authService: AuthService,
    private modalService: ModalService,
    private snackService: SnackService
  ) {
    this.availableOptions = Object.keys(ImmoTaskType).filter((v) =>
      isNaN(Number(v))
    );
  }

  async ngOnInit() {
    this.placerChoices =
      await this.ipInstallerListControllerRequestService.IPInstallerList();
    this.getEmployees();
    this.initializeTask();
  }

  ngOnDestroy(): void {
    this.cleanupOfflineAssets();
  }

  private initializeTask(): void {
    if (this.taskId == null) {
      this.task = getNewEditable();
      this.initializePresets();
      this.selected = this.task.type;
      this.loading = false;
    } else {
      this.getImmoTaskForEditControllerRequestService
        .getForEdit(this.taskId)
        .then((task: ImmoTaskForEdit) => {
          this.task = getImmoTaskEditableFromForEdit(task);
          if (!!this.task) {
            if (!!this.task.executionTime) {
              this.executionTime = this.task.executionTime.slice(0, 5);
            }
            if (this.task.id != null && this.placerChoices != null) {
              this.placer = this.placerChoices.find(
                (p) => p.id === this.task.placerId
              );
            }
          }
          this.loading = false;
          this.selected = task.type;
        });
    }
  }

  private getEmployees(): void {
    this.allEmployeesControllerRequestService
      .getAllEmployees()
      .then((employees: Employee[]) => {
        this.employeeChoices = employees;
        if (this.taskId == null) {
          this.trySelectCurrentUser();
        }
      });
  }

  private trySelectCurrentUser(): void {
    if (CollectionHelper.isPopulated(this.employeeChoices)) {
      this.authService
        .waitUntilAuthenticatedUserLoadedSubscribable()
        .toPromise()
        .then((currentUser: User) => {
          const matchingEmployee = this.employeeChoices.find(
            (e) => e.user != null && e.user.id === currentUser.id
          );
          if (matchingEmployee != null) {
            this.task.requestingUserId = matchingEmployee.user.id;
          }
        });
    }
  }

  private initializePresets(): void {
    if (this.placerInput != null) {
      this.placer = this.placerInput;
      this.task.placerId = this.placerInput.id;
    }

    if (this.tourDate != null) {
      this.task.tourDate = this.tourDate.toDate();
    }
  }

  private static readonly ALL_INSTALLERS_PLACER_ID = -1;

  placerChanged(placerId: number): void {
    if (
      placerId != null &&
      placerId !== CreateEditTaskModalComponent.ALL_INSTALLERS_PLACER_ID &&
      this.placerChoices != null
    ) {
      this.placer = this.placerChoices.find((p) => p.id === placerId);
    } else {
      this.placer = null; // "Bij alle installateurs", "Geen installateur", or no selection
    }
  }

  requestedASAPChanged(asap: boolean): void {
    this.task.requestedASAP = asap;
    if (asap) {
      this.task.requestedDate = null;
    }
  }

  requestedDateChanged(requestedDate: Date): void {
    if (requestedDate != null) {
      this.task.requestedASAP = false;
    }
  }

  resetTour(): void {
    this.task.placerId = null;
    this.task.tourDate = null;
  }

  executedChanged(executed: boolean): void {
    if (!executed) {
      this.task.executed = null;
    }
  }

  closeDialog(): void {
    this.matDialogRef.close();
  }

  archive(): void {
    this.modalService.confirmDialog(
      'Taak wissen',
      'Bent u zeker dat u deze taak wilt wissen?',
      (b: boolean) => {
        if (b) {
          this.archiving = true;
          this.immoTaskOverviewControllerRequestService
            .archive(this.taskId)
            .then(() => {
              this.result.emit('taskArchived');
              this.snackService.showSuccess('Taak succesvol gewist');
            })
            .finally(() => (this.archiving = false));
        }
      }
    );
  }

  saveTask(): void {
    if (this.task.requestingUserId == null) {
      this.snackService.showError('Gelieve een aanvrager te selecteren');
      return;
    }
    if (StringHelper.isEmptyOrWhiteSpace(this.task.subject)) {
      this.snackService.showError('Gelieve een onderwerp in te vullen');
      return;
    }
    // Validate that if an installer is selected, there must be a tour date
    if (this.task.placerId != null && this.task.tourDate == null) {
      this.snackService.showError(
        'Voeg een uitvoeringsdatum toe of deselecteer installateur'
      );
      return;
    }
    this.task.type = this.selected;
    this.submitting = true;
    this.doSaveTask()
      .then(() => {
        if (
          this.task.placerId ===
          CreateEditTaskModalComponent.ALL_INSTALLERS_PLACER_ID
        ) {
          this.snackService.showSuccess(
            'Taken succesvol aangemaakt voor alle installateurs'
          );
        } else {
          this.snackService.showSuccess('Taak succesvol opgeslagen');
        }
        this.result.emit('taskCreatedOrEdited');
      })
      .finally(() => (this.submitting = false));
  }

  updateExecutionTime(time: string) {
    this.task.executionTime = time;
  }

  private async doSaveTask(): Promise<void> {
    const clonedTask: ImmoTaskEditable = cloneDeep(this.task);

    const fillBase64Promises = [
      ...this.fillOfflineAssetBase64ForOfflineAssets(
        clonedTask.offlineDesignAssets
      ),
      ...this.fillOfflineAssetBase64ForOfflineAssets(
        clonedTask.offlineLocationAssets
      ),
      ...this.fillOfflineAssetBase64ForOfflineAssets(
        clonedTask.offlineExecutionAssets
      ),
    ];
    await Promise.all(fillBase64Promises);

    await this.createEditImmoTaskControllerRequestService.createEditTask(
      clonedTask
    );
  }

  private fillOfflineAssetBase64ForOfflineAssets(
    offlineAssets: Asset[]
  ): Promise<void>[] {
    return CollectionHelper.isPopulated(offlineAssets)
      ? offlineAssets.map((oa) => this.fillOfflineAssetBase64(oa))
      : [];
  }

  private async fillOfflineAssetBase64(offlineAsset: Asset): Promise<void> {
    if (!StringHelper.isEmpty(offlineAsset.base64)) {
      return;
    }
    const file = await this.ngForage.getItem<File>(
      AssetHelper.getStorageKeyFor(offlineAsset.localFileUuid)
    );
    offlineAsset.base64 = await AssetHelper.extractBase64(file);
  }

  private cleanupOfflineAssets(): void {
    if (
      !CollectionHelper.isPopulated(this.task.offlineDesignAssets) &&
      !CollectionHelper.isPopulated(this.task.offlineExecutionAssets) &&
      !CollectionHelper.isPopulated(this.task.offlineLocationAssets)
    ) {
      return;
    }

    this.doCleanupOfflineAssets('offlineDesignAssets');
    this.doCleanupOfflineAssets('offlineExecutionAssets');
    this.doCleanupOfflineAssets('offlineLocationAssets');
  }

  private doCleanupOfflineAssets(
    prop: KeysOfType<ImmoTaskEditable, Asset[]>
  ): void {
    if (CollectionHelper.isPopulated(this.task[prop])) {
      this.task[prop].forEach((a) =>
        this.ngForage.removeItem(AssetHelper.getStorageKeyFor(a.localFileUuid))
      );
    }
  }

  getOptionLabel(type: string) {
    switch (type) {
      case 'TASK':
        return 'Taak';
      case 'COLLECT_KEY':
        return 'Sleutel';
      default:
        throw new Error('Unsupported option');
    }
  }
}
